import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, generateWhatsAppLink } from "../utils/api";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Lock, Play } from "lucide-react";
import { DiamondLoader } from "../components/DiamondLoader";
import VideoWithThumbnail from "../components/VideoWithThumbnail";

export const DiamondDetail = () => {
  var IMGURL = "https://justinndiam.com:8000";
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [diamond, setDiamond] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  const canViewPrice =
    isAuthenticated && (user?.status === "approved" || user?.role === "admin");

  useEffect(() => {
    fetchDiamond();
  }, [id, canViewPrice]);

  const fetchDiamond = async () => {
    try {
      const response = canViewPrice
        ? await api.getDiamond(id)
        : await api.getPublicDiamond(id);
      setDiamond(response.data);
    } catch (error) {
      console.error("Failed to fetch diamond:", error);
      toast.error("Failed to load diamond details");
      navigate("/catalog");
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    window.open(generateWhatsAppLink(diamond), "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <DiamondLoader size="lg" />
      </div>
    );
  }

  if (!diamond) {
    return null;
  }

  const allMedia = [
    ...(diamond.images || []).map((img) => ({ type: "image", url: img })),
    ...(diamond.videos || []).map((vid) => ({ type: "video", url: vid })),
  ];

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-24 py-12">
      <button
        onClick={() => navigate("/catalog")}
        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8 uppercase tracking-widest text-xs font-bold"
        data-testid="back-to-catalog-btn"
      >
        <ArrowLeft size={16} />
        Back to Catalog
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Media Gallery */}
        <div className="space-y-6" data-testid="diamond-media-gallery">
          <div className="aspect-square bg-surface relative overflow-hidden">
            {allMedia.length > 0 ? (
              allMedia[selectedImage].type === "image" ? (
                <img
                  src={IMGURL + allMedia[selectedImage].url}
                  alt="Diamond"
                  className="w-full h-full object-cover"
                />
              ) : (
                <video
                  src={IMGURL+allMedia[selectedImage].url}
                  controls
                  className="w-full h-full"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                No media available
              </div>
            )}
          </div>

          {allMedia.length > 1 && (
            <div className="grid grid-cols-5 gap-4">
              {allMedia.map((media, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`aspect-square bg-surface overflow-hidden relative ${
                    selectedImage === index
                      ? "border-2 border-primary"
                      : "border border-border hover:border-primary/50"
                  } transition-colors`}
                  data-testid={`media-thumbnail-${index}`}
                >
                  {media.type === "image" ? (
                    <img
                      src={IMGURL + media.url}
                      alt="Thumbnail"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-secondary/10">
                      <VideoWithThumbnail src={IMGURL + media.url} />
                      {/* <Play className="text-primary" size={20} /> */}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="space-y-8" data-testid="diamond-details">
          <div>
            <h1 className="font-heading text-4xl font-semibold mb-2">
              {diamond.shape} Diamond
            </h1>
            <p className="text-muted-foreground font-mono tabular-nums">
              {diamond.size} Carat
            </p>
            <p className="flex">
              <p className="uppercase tracking-widest font-semibold mr-5">
                Stock Id
              </p>{" "}
              {diamond.stock_id}
            </p>
          </div>

          {canViewPrice && diamond.price_per_carat ? (
            <div className="space-y-4 py-6 border-y border-border">
              <div className="flex items-end justify-between">
                <span className="text-sm uppercase tracking-widest text-muted-foreground">
                  Price per Carat
                </span>
                <span className="font-mono text-2xl font-bold tabular-nums">
                  ${diamond.price_per_carat.toLocaleString()}
                </span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-sm uppercase tracking-widest text-muted-foreground">
                  Rap Discount
                </span>
                <span className="font-mono text-xl tabular-nums text-primary">
                  {diamond.rap_discount}%
                </span>
              </div>
              <div className="flex items-end justify-between pt-4 border-t border-border">
                <span className="uppercase tracking-widest font-semibold">
                  Total Amount
                </span>
                <span className="font-mono text-3xl font-bold text-primary tabular-nums">
                  ${diamond.total_amount.toLocaleString()}
                </span>
              </div>
            </div>
          ) : (
            <div className="py-8 border-y border-border text-center space-y-4">
              <Lock className="mx-auto text-muted-foreground" size={48} />
              <p className="text-muted-foreground">
                <Link
                  to="/register"
                  className="text-primary hover:underline font-semibold"
                >
                  Register
                </Link>{" "}
                to view pricing
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6">
            <div>
              <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                Color
              </span>
              <span className="font-semibold text-lg">{diamond.colour}</span>
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                Clarity
              </span>
              <span className="font-semibold text-lg">{diamond.clarity}</span>
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                Cut
              </span>
              <span className="font-semibold text-lg">{diamond.cut}</span>
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                Polish
              </span>
              <span className="font-semibold text-lg">{diamond.polish}</span>
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                Symmetry
              </span>
              <span className="font-semibold text-lg">{diamond.symmetry}</span>
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                Lab
              </span>
              <span className="font-semibold text-lg">{diamond.lab}</span>
            </div>
          </div>

          {canViewPrice && (
            <>
              <div className="grid grid-cols-2 gap-6 pt-6 border-t border-border">
                {diamond.measurement && (
                  <div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                      Measurement
                    </span>
                    <span className="font-mono text-sm tabular-nums">
                      {diamond.measurement}
                    </span>
                  </div>
                )}
                {diamond.fluorescence && (
                  <div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                      Fluorescence
                    </span>
                    <span className="text-sm">{diamond.fluorescence}</span>
                  </div>
                )}
                {diamond.certificate && (
                  <div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                      Certificate
                    </span>
                    <span className="text-sm">{diamond.certificate}</span>
                  </div>
                )}
                {diamond.location && (
                  <div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                      Location
                    </span>
                    <span className="text-sm">{diamond.location}</span>
                  </div>
                )}
                {diamond.shade && (
                  <div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                      Shade
                    </span>
                    <span className="text-sm">{diamond.shade}</span>
                  </div>
                )}
                {diamond.ratio && (
                  <div>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                      Ratio
                    </span>
                    <span className="text-sm">{diamond.ratio}</span>
                  </div>
                )}
              </div>

              {diamond.comment && (
                <div className="pt-6 border-t border-border">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-3">
                    Comments
                  </span>
                  <p className="text-sm leading-relaxed">{diamond.comment}</p>
                </div>
              )}

              <div className="flex gap-4 pt-6">
                {diamond.no_bgm && (
                  <div className="border border-primary px-4 py-2">
                    <span className="text-xs uppercase tracking-widest text-primary font-semibold">
                      No BGM
                    </span>
                  </div>
                )}
                {diamond.eye_clean && (
                  <div className="border border-primary px-4 py-2">
                    <span className="text-xs uppercase tracking-widest text-primary font-semibold">
                      Eye Clean
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          <button
            onClick={handleBuyNow}
            className="w-full bg-primary text-white hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-5 transition-all duration-300 flex items-center justify-center gap-3"
            data-testid="buy-now-btn"
          >
            <MessageCircle size={20} />
            Inquire via WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};
